import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity } from "lucide-react";
import { toast } from "sonner";
import { ACTION_LABELS, api, errMsg, fmtDateTime } from "../lib/api";
import { EmptyState, PageHeader } from "../components/ui-bits";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function ActivityPage() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (projectFilter !== "all") params.project_id = projectFilter;
      const { data } = await api.get("/activity", { params });
      setItems(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [projectFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data);
      } catch {}
    })();
  }, []);

  return (
    <div className="animate-fade-up" data-testid="activity-page">
      <PageHeader overline="Protokoll" title="Aktivität" testid="activity-header">
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger data-testid="activity-project-filter" className="h-9 w-56 rounded-xl bg-white text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Projekte</SelectItem>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PageHeader>

      {loading ? (
        <div className="h-64 animate-pulse rounded-2xl bg-white" />
      ) : items.length === 0 ? (
        <EmptyState icon={Activity} title="Noch keine Aktivitäten" hint="Alle Aktionen im Control Center werden hier protokolliert." testid="activity-empty" />
      ) : (
        <div className="rounded-2xl border border-[#1D225C]/10 bg-white p-5 shadow-sm" data-testid="activity-list">
          <div className="space-y-4">
            {items.map((a) => (
              <div key={a.id} data-testid={`activity-item-${a.id}`} className="flex gap-3">
                <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#B88A4A]" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[#1D225C]">
                    <span className="font-semibold">{a.user_name}</span>
                    <span className="text-[#777]"> · {ACTION_LABELS[a.action] || a.action}</span>
                  </p>
                  {a.details && (
                    <p
                      className={`text-sm text-[#555] ${a.project_id ? "cursor-pointer hover:text-[#B88A4A]" : ""}`}
                      onClick={() => a.project_id && navigate(`/projekte/${a.project_id}`)}
                    >
                      {a.details}
                    </p>
                  )}
                  <p className="text-xs text-[#999]">{fmtDateTime(a.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
